import {
  Menu,
  MenuButton,
  MenuList,
  Button,
  Text,
  Flex,
  useMediaQuery,
  useDisclosure,
  Input,
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
} from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { useState, useRef, useCallback, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import FigmaEditIcon from 'components/Icons/FigmaEditIcon';
import FigmaMenuDotsIcon from 'components/Icons/FigmaMenuDotsIcon';
import FigmaMoveIcon from 'components/Icons/FigmaMoveIcon';
import FigmaAddPageIcon from 'components/Icons/FigmaAddPageIcon';
import FigmaPlusIcon from 'components/Icons/FigmaPlusIcon';
import FigmaSearchIcon from 'components/Icons/FigmaSearchIcon';
import FigmaTrashIcon from 'components/Icons/FigmaTrashIcon';
import FigmaTriangleDown from 'components/Icons/FigmaTriangleDown';
import { AppDispatch } from 'utils/GlobalHelpers';
import {
  accent,
  controlHeight,
  font,
  fontSize,
  neutral,
  primaryAction,
  radius,
  shadow,
  text as textToken,
} from 'styles/revampTokens';
import {
  prepareCreatePayload,
  getDashboard,
  setDashboard,
  getIsPageBarOpen,
  updatePageOrder,
  getSelectedPage,
  setSelectedPage,
  openPageModal,
  deletePage,
  reorderPages,
  getAllInstruments,
  setTimeFilter,
} from 'redux/DashboardSliceV2';

import DeleteModal from './components/DeleteModal';
import ExportButtonNew from './Export/ExportButtonNew';
import InstrumentMenuList from './InstrumentMenuList';
import SelectScenario from './SelectScenario';
import TimeRangeFilter from './TimeRangeFilter';

const Header = () => {
  const dispatch: AppDispatch = useDispatch();
  const isPageBarOpen = useSelector(getIsPageBarOpen);
  const [smallDevice] = useMediaQuery('(max-width: 1100px)');
  const isSmallDevice = (smallDevice && isPageBarOpen) || smallDevice;
  const dashboard = useSelector(getDashboard);
  const selectedPage = useSelector(getSelectedPage);
  // Only the *selected* page's widgets — fetchDashboardMetadata is called
  // per-page (dashboardPageMetadata(selectedPage?.id) in index.tsx), not once
  // for the whole dashboard, so there's no cross-page instrument list to
  // search here without a new API call. Searching what's actually loaded is
  // still a real improvement over the page list alone: the common case is
  // "I'm on a page, where's the widget I'm looking for", not "which page has
  // it" — that broader case needs a backend change to fetch every page's
  // instruments up front, which is out of scope for this pass.
  const allInstruments = useSelector(getAllInstruments) || [];
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [pageSearch, setPageSearch] = useState('');
  const [pageToDelete, setPageToDelete] = useState<any>(null);
  const {
    isOpen: isDeleteModalOpen,
    onOpen: openDeleteModal,
    onClose: closeDeleteModal,
  } = useDisclosure();

  const pages = dashboard?.pages || [];
  const sortedPages = [...pages]
    .sort((a: any, b: any) => a.position - b.position)
    .filter((page: any) => (page.name || '').toLowerCase().includes(pageSearch.toLowerCase()));

  // Widgets on the current page whose title matches the same search term —
  // shown as its own short list under the page list so a search for a
  // component's name (not a page's name) surfaces something instead of just
  // silently filtering the page list down to nothing.
  const matchingInstruments = pageSearch.trim()
    ? allInstruments.filter((instrument: any) =>
        (instrument?.name || '').toLowerCase().includes(pageSearch.toLowerCase()),
      )
    : [];

  const scrollToInstrument = (instrumentId: string | number) => {
    const element = document.querySelector<HTMLElement>(`#instrument-${instrumentId}`);
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Same outline-pulse idea as the Dimensions table's new-row/new-column
    // cues — a brief "look here" glow, not a persistent state, so it's driven
    // directly via WAAPI rather than adding a CSS class + timer pair for a
    // one-off, single-element case.
    element.animate(
      [
        { outline: '2px solid rgba(139, 92, 246, 0)', outlineOffset: '2px' },
        { outline: '2px solid rgba(139, 92, 246, 0.85)', outlineOffset: '2px' },
        { outline: '2px solid rgba(139, 92, 246, 0)', outlineOffset: '2px' },
      ],
      { duration: 700, iterations: 2, easing: 'ease-in-out' },
    );
  };

  const setSelectedPageAndPersist = (page: any) => {
    dispatch(setSelectedPage(page));
    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    data[dashboard.model_id] = {
      ...data[dashboard.model_id],
      selectedPageId: page?.id,
    };
    localStorage.setItem('global-modal-filters', JSON.stringify(data));
  };

  const handleCreatePage = () => {
    dispatch(openPageModal({}));
  };

  const handleEditPage = (page: any) => {
    dispatch(openPageModal(page));
  };

  const handleDeletePage = (page: any) => {
    setPageToDelete(page);
    openDeleteModal();
  };

  const confirmDeletePage = () => {
    if (pageToDelete) {
      dispatch(deletePage({ pageId: pageToDelete.id }));
      setPageToDelete(null);
      closeDeleteModal();
    }
  };

  const cancelDeletePage = () => {
    setPageToDelete(null);
    closeDeleteModal();
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    const displayedPages = [...sortedPages].sort((a: any, b: any) => a.position - b.position);
    const reorderedPages = Array.from(displayedPages);
    const [movedPage] = reorderedPages.splice(sourceIndex, 1);
    reorderedPages.splice(destinationIndex, 0, movedPage);

    const updatedPages = reorderedPages.map((page, index) => ({
      ...(page as any),
      position: index,
    }));

    const nonFilteredPages = pages.filter(
      (page: any) => !sortedPages.some((sp: any) => sp.id === page.id),
    );
    const allUpdatedPages = [...nonFilteredPages, ...updatedPages];

    dispatch(reorderPages(allUpdatedPages));

    try {
      const updatedDashboard = {
        ...dashboard,
        pages: allUpdatedPages,
      };
      await dispatch(updatePageOrder({ dashboard: updatedDashboard }));
    } catch {
      dispatch(reorderPages(pages));
    }
  };

  const handleNameDoubleClick = () => {
    setEditedName(dashboard?.name || '');
    setIsEditingName(true);
  };

  const handleNameSave = async () => {
    if (dashboard && editedName.trim()) {
      try {
        const updatedDashboard = {
          ...dashboard,
          name: editedName.trim(),
        };
        await dispatch(updatePageOrder({ dashboard: updatedDashboard })).then(() => {
          dispatch(setDashboard(updatedDashboard));
        });
      } catch {
        // Handle error if needed
      }
    }
    setIsEditingName(false);
  };

  const handleNameKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleNameSave();
    } else if (event.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  const onSelectInstrument = (widgetType: string) => dispatch(prepareCreatePayload(widgetType));

  const rangeSelected = (start: string, end: string) => {
    if (dashboard?.id) {
      dispatch(setTimeFilter({ start, end }));
    }
  };

  const scrollReference = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const element = scrollReference.current;
    if (!element) return;
    setCanScrollLeft(element.scrollLeft > 0);
    setCanScrollRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const element = scrollReference.current;
    if (!element) return () => {};
    // Check overflow after layout settles
    const timer = setTimeout(updateScrollState, 50);
    element.addEventListener('scroll', updateScrollState, { passive: true });
    return () => {
      clearTimeout(timer);
      element.removeEventListener('scroll', updateScrollState);
    };
  }, [updateScrollState, pages.length]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (scrollReference.current) {
      scrollReference.current.scrollBy({
        left: direction === 'left' ? -160 : 160,
        behavior: 'smooth',
      });
    }
  }, []);
  return (
    // Same rhythm as blocksv2's BlockHeader: mx='-3'/px='3' (12px) horizontal
    // inset, 12px vertical padding. index.tsx's outer wrapper was moved to
    // px={3} to match, so this and the content grid below it stay in step.
    <Box
      bg={neutral.surface}
      borderBottom={`1px solid ${neutral.border}`}
      mx='-3'
      px='3'
      py='12px'
      display='flex'
      flexDirection='column'
      gap='16px'
    >
      <Flex maxW={'100vw'} justifyContent={'space-between'} alignItems='center' gap='12px'>
        <Flex alignItems='center' gap='16px'>
          {isEditingName ? (
            <Input
              value={editedName}
              onChange={(event) => setEditedName(event.target.value)}
              onBlur={handleNameSave}
              onKeyDown={handleNameKeyPress}
              color={textToken.primary}
              fontSize={fontSize.title}
              fontWeight='600'
              fontFamily={font.display}
              bg={neutral.surface}
              border={`1px solid ${neutral.border}`}
              _focus={{
                borderColor: accent.solid,
                boxShadow: 'none',
              }}
              maxWidth='450px'
              width='fit-content'
              autoFocus
              height='auto'
              minHeight='36px'
            />
          ) : (
            <Text
              alignContent='center'
              color={textToken.primary}
              fontSize={fontSize.title}
              whiteSpace='nowrap'
              overflow='hidden'
              fontWeight='600'
              fontFamily={font.display}
              maxWidth='450px'
              textOverflow='ellipsis'
              cursor='pointer'
              onDoubleClick={handleNameDoubleClick}
              _hover={{ opacity: 0.8 }}
              title='Double-click to edit dashboard name'
            >
              {dashboard?.name}
            </Text>
          )}
          {/* Scenario + date read as one control, exactly like Plan/Dimensions
              revamp: one bordered pill, the two halves meeting inside it.
              SelectScenario/TimeRangeFilter already carry the matching
              9px-0-0-9px / 0-9px-9px-0 radii for this — they were only ever
              missing the shared outer border to actually look like one. */}
          <Flex
            alignItems='stretch'
            border='1px solid'
            borderColor={neutral.border}
            borderRadius={radius.control}
            overflow='hidden'
            h={controlHeight}
            flexShrink={0}
          >
            <SelectScenario />
            {dashboard?.id && <TimeRangeFilter rangeSelected={rangeSelected} />}
          </Flex>
        </Flex>
        <Flex gap='16px' alignItems='center'>
          {/* Same shape as blocksv2's HeaderControls search field — bordered
              neutral pill, icon and placeholder in the muted token, accent
              reserved for focus rather than sitting on the idle icon.

              Wrapped in a Popover now: searching used to only ever filter the
              page-name strip below, so typing a widget's name (not a page's)
              silently filtered that strip down to nothing with no other
              feedback. This surfaces matching widgets on the *current* page —
              clicking one scrolls to and glows it (scrollToInstrument above).
              Cross-page widget search needs a backend change (see that
              function's comment) and isn't covered here. */}
          <Popover
            isOpen={Boolean(pageSearch.trim() && matchingInstruments.length > 0)}
            placement='bottom-start'
            autoFocus={false}
            isLazy
          >
            <PopoverTrigger>
              <Box
                display={{ base: 'none', xl: 'flex' }}
                alignItems='center'
                gap='7px'
                bg={neutral.surface}
                border={`1px solid ${neutral.border}`}
                borderRadius={radius.control}
                height={controlHeight}
                px='12px'
                width='217px'
                transition='border-color 0.12s ease'
                _hover={{ borderColor: neutral.borderStrong }}
                _focusWithin={{ borderColor: neutral.borderStrong }}
              >
                <FigmaSearchIcon width={14} height={14} color={textToken.muted} />
                <Input
                  variant='unstyled'
                  placeholder='Search dashboard page or widget'
                  value={pageSearch}
                  onChange={(event) => setPageSearch(event.target.value)}
                  fontSize={fontSize.body}
                  fontWeight='500'
                  fontFamily={font.body}
                  color={textToken.primary}
                  _placeholder={{ color: textToken.muted }}
                />
              </Box>
            </PopoverTrigger>
            <PopoverContent
              width='217px'
              bg={neutral.surface}
              border={`1px solid ${neutral.border}`}
              borderRadius={radius.overlay}
              boxShadow={shadow.overlay}
              overflow='hidden'
              p={0}
              _focusVisible={{ boxShadow: shadow.overlay }}
            >
              <PopoverBody p={0}>
                <Text
                  px='12px'
                  pt='10px'
                  pb='4px'
                  fontSize='11px'
                  fontWeight='600'
                  fontFamily={font.body}
                  color={textToken.muted}
                  textTransform='uppercase'
                  letterSpacing='0.02em'
                >
                  Widgets on this page
                </Text>
                <Flex direction='column' maxH='220px' overflowY='auto'>
                  {matchingInstruments.map((instrument: any) => (
                    <Flex
                      key={instrument.id}
                      as='button'
                      onClick={() => {
                        scrollToInstrument(instrument.id);
                        setPageSearch('');
                      }}
                      alignItems='center'
                      px='12px'
                      py='8px'
                      transition='background 0.12s ease'
                      _hover={{ bg: neutral.surfaceHover }}
                    >
                      <Text
                        fontSize={fontSize.body}
                        fontFamily={font.body}
                        color={textToken.primary}
                        whiteSpace='nowrap'
                        overflow='hidden'
                        textOverflow='ellipsis'
                      >
                        {instrument.name}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              </PopoverBody>
            </PopoverContent>
          </Popover>
          {/* <ExportButton /> */}
          <ExportButtonNew />
          {/* Prop-for-prop the same trigger as blocksv2/HeaderControls's
              "Create block" and DimensionsRevamp's "Create" — primaryAction
              (near-black) rather than the purple accent, same control height
              and corner. */}
          <Menu variant={'dashboarding'}>
            {({ isOpen }) => {
              return (
                <>
                  <MenuButton
                    backgroundColor={primaryAction.bg}
                    padding='0 14px'
                    borderRadius={radius.control}
                    as={Button}
                    title={'Create'}
                    border={'none'}
                    _hover={{ backgroundColor: primaryAction.hover }}
                    _active={{ backgroundColor: primaryAction.hover }}
                    rightIcon={<FigmaTriangleDown width={9} height={5} color={textToken.inverse} />}
                    leftIcon={<FigmaPlusIcon width={13} height={13} color={textToken.inverse} />}
                    fontSize={fontSize.body}
                    fontWeight={'500'}
                    fontFamily={font.body}
                    color={textToken.inverse}
                    width={'fit-content'}
                    height={controlHeight}
                  >
                    {isSmallDevice ? '' : 'Create'}
                  </MenuButton>
                  {isOpen && (
                    <MenuList
                      zIndex={18}
                      p={0}
                      minW='153px'
                      bg={neutral.surface}
                      border={`1px solid ${neutral.border}`}
                      borderRadius={radius.overlay}
                      overflow='hidden'
                      boxShadow={shadow.overlay}
                    >
                      <InstrumentMenuList onSelectInstrument={onSelectInstrument} />
                    </MenuList>
                  )}
                </>
              );
            }}
          </Menu>
        </Flex>
      </Flex>

      {/* Same geometry as blocksv2's category strip (categoryStripTokens.ts):
          32px pill height, 12px inset, 10px corner, 13px label, tabs flush
          with no gap, accent only on the selected pill. No "PAGES" caption —
          the strip reads as navigation on its own, the same way the category
          strip needs no label above it. */}
      {pages.length > 0 && (
        <Flex align='center' gap='4px'>
          {/* Left arrow — only when scrolled right */}
          {canScrollLeft && (
            <Box
              as='button'
              onClick={() => scroll('left')}
              display='flex'
              alignItems='center'
              justifyContent='center'
              flexShrink={0}
              width='24px'
              height={controlHeight}
              borderRadius={radius.control}
              color={textToken.muted}
              _hover={{ bg: neutral.surfaceHover, color: textToken.primary }}
              transition='background 0.12s ease, color 0.12s ease'
            >
              <svg width='14' height='14' viewBox='0 0 16 16' fill='none'>
                <path
                  d='M10 4L6 8l4 4'
                  stroke='currentColor'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </Box>
          )}

          {/* Scrollable pages strip */}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId='pages-strip' direction='horizontal'>
              {(provided) => (
                <Flex
                  align='center'
                  wrap='nowrap'
                  gap='0px'
                  overflowX='auto'
                  flex='1 1 0'
                  minW={0}
                  sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
                  ref={(node: any) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (scrollReference as any).current = node;
                    provided.innerRef(node);
                  }}
                  {...provided.droppableProps}
                >
                  {sortedPages.map((page: any, index: number) => {
                    const isSelected = selectedPage?.id === page.id;
                    return (
                      <Draggable key={page.id} draggableId={String(page.id)} index={index}>
                        {(dragProvided) => (
                          <Flex
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            as='button'
                            role='group'
                            align='center'
                            height={controlHeight}
                            gap='11px'
                            onClick={() => setSelectedPageAndPersist(page)}
                            px='12px'
                            borderRadius='10px'
                            bg={isSelected ? accent.subtle : neutral.surface}
                            color={isSelected ? accent.solid : '#121826'}
                            fontFamily={font.body}
                            fontSize='13px'
                            fontWeight={isSelected ? '600' : '400'}
                            whiteSpace='nowrap'
                            _hover={{ bg: isSelected ? accent.subtle : neutral.surfaceHover }}
                            _active={{ transform: 'scale(0.97)' }}
                            transition='background-color 0.15s, transform 0.1s ease'
                          >
                            {page.name || `Page ${page.id}`}
                            {isSelected && (
                              <Box
                                {...dragProvided.dragHandleProps}
                                cursor='grab'
                                display='flex'
                                onClick={(event) => event.stopPropagation()}
                              >
                                <FigmaMoveIcon width={14} height={14} color={accent.solid} />
                              </Box>
                            )}
                            {/* A Popover, not Chakra's Menu. Menu focuses its
                                list with `preventScroll: false` on open and
                                always hands focus back to the trigger on close,
                                with no way to opt out. This trigger collapses to
                                0px the moment the menu shuts, so returning focus
                                to it scrolled the page strip out of view — the
                                jump on Edit/Delete. Popover focuses with
                                `preventScroll: true` and takes
                                `returnFocusOnClose`. */}
                            <Popover
                              placement='bottom-end'
                              isLazy
                              gutter={4}
                              returnFocusOnClose={false}
                              // Portalled content needs a viewport-relative
                              // frame, or it drifts when the strip scrolls.
                              strategy='fixed'
                            >
                              {({ isOpen: isPageMenuOpen, onClose: closePageMenu }: any) => {
                                // `isPageMenuOpen` is what stops the flicker:
                                // while the menu is open the pointer sits over
                                // the list, not the pill, so `_groupHover`
                                // drops and this trigger would animate back to
                                // width 0. Chakra re-anchors the popper to the
                                // shrinking trigger, which slides the menu off
                                // the pointer, hover returns, and the whole
                                // thing oscillates. Pinning the trigger open
                                // keeps the anchor a fixed size.
                                const showTrigger = isSelected || isPageMenuOpen;
                                return (
                                  <>
                                    <PopoverTrigger>
                                      <Box
                                        // A div, not a button: the pill that
                                        // wraps this is already a <button>.
                                        // role + aria-label give it the
                                        // accessible name it was missing.
                                        role='button'
                                        aria-label='Page options'
                                        aria-haspopup='menu'
                                        display='flex'
                                        alignItems='center'
                                        // Fixed box, fading contents. Animating
                                        // the width made this the popper's
                                        // anchor AND a moving target: hovering
                                        // the open menu drops `_groupHover`,
                                        // the trigger shrinks, Chakra re-anchors
                                        // the menu off the pointer, hover
                                        // returns, and it oscillates. A stable
                                        // 14px box has nothing to oscillate.
                                        width='14px'
                                        marginLeft='2px'
                                        flexShrink={0}
                                        opacity={showTrigger ? 1 : 0}
                                        pointerEvents={showTrigger ? 'auto' : 'none'}
                                        transition='opacity 0.15s ease'
                                        _groupHover={{ opacity: 1, pointerEvents: 'auto' }}
                                        cursor='pointer'
                                        onClick={(event: any) => event.stopPropagation()}
                                      >
                                        {/* secondary (not muted) is dark enough to
                                        register as a real affordance on the
                                        resting pill without going oversized. */}
                                        <FigmaMenuDotsIcon
                                          width={14}
                                          height={14}
                                          color={
                                            isSelected || isPageMenuOpen
                                              ? accent.solid
                                              : textToken.secondary
                                          }
                                        />
                                      </Box>
                                    </PopoverTrigger>
                                    {/* Portalled. Rendered in place, the menu
                                        sits inside the page pill — which is a
                                        <button> — so its own <button> rows were
                                        nested buttons and the browser resolved
                                        the click to the pill instead: the menu
                                        just closed and selected the page. The
                                        pages strip is `overflow-x: auto` too,
                                        which clips it vertically. */}
                                    <Portal>
                                      <PopoverContent
                                        zIndex={18}
                                        width='153px'
                                        minWidth='153px'
                                        bg={neutral.surface}
                                        border={`1px solid ${neutral.border}`}
                                        borderRadius={radius.overlay}
                                        overflow='hidden'
                                        boxShadow={shadow.overlay}
                                        outline='none'
                                        _focus={{ boxShadow: shadow.overlay }}
                                        _focusVisible={{ boxShadow: shadow.overlay }}
                                      >
                                        {/* Popover is the mechanism, but this is a
                                          menu of actions — keep the roles that
                                          describes so screen readers (and the
                                          tests) still see one. */}
                                        <PopoverBody p={0} role='menu'>
                                          <Flex
                                            as='button'
                                            type='button'
                                            role='menuitem'
                                            width='100%'
                                            alignItems='center'
                                            bg={neutral.surface}
                                            gap='8px'
                                            p='9px 11px'
                                            fontFamily={font.body}
                                            fontSize='13px'
                                            color={textToken.primary}
                                            _hover={{ bg: neutral.surfaceHover }}
                                            onClick={(event: any) => {
                                              event.stopPropagation();
                                              closePageMenu();
                                              handleEditPage(page);
                                            }}
                                          >
                                            <FigmaEditIcon
                                              width={14}
                                              height={14}
                                              color={textToken.primary}
                                            />
                                            Edit
                                          </Flex>
                                          {pages.length > 1 && (
                                            <Flex
                                              as='button'
                                              type='button'
                                              role='menuitem'
                                              width='100%'
                                              alignItems='center'
                                              bg={neutral.surface}
                                              gap='8px'
                                              p='9px 11px'
                                              fontFamily={font.body}
                                              fontSize='13px'
                                              color={textToken.primary}
                                              _hover={{ bg: neutral.surfaceHover }}
                                              onClick={(event: any) => {
                                                event.stopPropagation();
                                                closePageMenu();
                                                handleDeletePage(page);
                                              }}
                                            >
                                              <FigmaTrashIcon
                                                width={14}
                                                height={14}
                                                color={textToken.primary}
                                              />
                                              Delete
                                            </Flex>
                                          )}
                                        </PopoverBody>
                                      </PopoverContent>
                                    </Portal>
                                  </>
                                );
                              }}
                            </Popover>
                          </Flex>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </Flex>
              )}
            </Droppable>
          </DragDropContext>

          {/* Right arrow — only when more pages overflow */}
          {canScrollRight && (
            <Box
              as='button'
              onClick={() => scroll('right')}
              display='flex'
              alignItems='center'
              justifyContent='center'
              flexShrink={0}
              width='24px'
              height={controlHeight}
              borderRadius={radius.control}
              color={textToken.muted}
              _hover={{ bg: neutral.surfaceHover, color: textToken.primary }}
              transition='background 0.12s ease, color 0.12s ease'
            >
              <svg width='14' height='14' viewBox='0 0 16 16' fill='none'>
                <path
                  d='M6 4l4 4-4 4'
                  stroke='currentColor'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </Box>
          )}

          {/* Add new Page — always fixed at end */}
          <Box
            as='button'
            onClick={handleCreatePage}
            display='flex'
            alignItems='center'
            flexShrink={0}
            height={controlHeight}
            gap='8px'
            px='12px'
            borderRadius='8px'
            color={accent.solid}
            fontFamily={font.body}
            fontSize='13px'
            whiteSpace='nowrap'
            transition='transform 0.1s ease'
            _active={{ transform: 'scale(0.94)' }}
          >
            <FigmaAddPageIcon width={16} height={16} />
            Add new Page
          </Box>
        </Flex>
      )}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={cancelDeletePage}
        itemName={pageToDelete?.name || `Page ${pageToDelete?.id}`}
        onDelete={confirmDeletePage}
      />
    </Box>
  );
};

export default Header;
